import axios from "axios";

// Helpers for exports that must load every record of a list without one huge
// request: fetch page by page, a few requests at a time, with retries.

const CONCURRENCY = 3; // requests in flight at once
const MAX_RETRIES = 2;

export async function withRetry(request, signal) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await request();
    } catch (err) {
      if (axios.isCancel(err) || signal?.aborted || attempt >= MAX_RETRIES) {
        throw err;
      }
      await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
    }
  }
}

// Runs `worker(item)` over `items` with at most CONCURRENCY in flight,
// keeping results in the same order as `items`.
export async function runInPool(items, worker) {
  const results = new Array(items.length);
  let next = 0;
  const lanes = Array.from(
    { length: Math.min(CONCURRENCY, items.length) },
    async () => {
      while (next < items.length) {
        const idx = next++;
        results[idx] = await worker(items[idx]);
      }
    },
  );
  await Promise.all(lanes);
  return results;
}

export function chunk(list, size) {
  const chunks = [];
  for (let i = 0; i < list.length; i += size) {
    chunks.push(list.slice(i, i + size));
  }
  return chunks;
}

// Loads every page of a paginated list, then the per-record details in
// chunks, reporting progress for ExportOptionsModal.
//
//   fetchPage(page)      -> { rows, totalPages }   (page starts at 1)
//   fetchDetails(rows)   -> { [id]: detail }        (one chunk of rows; optional,
//                           leave it out for lists with no details to load)
//   recordLabel          -> e.g. "invoices", used in the progress text
//   onProgress({ label, percent }) - pages fill 0-50%, details 50-100%
//                                    (pages fill 0-100% without fetchDetails)
//
// Returns { rows, details } where details is the merged lookup.
export async function fetchPagedWithDetails({
  fetchPage,
  fetchDetails,
  recordLabel,
  detailChunkSize = 200,
  signal,
  onProgress,
}) {
  const call = (request) => withRetry(request, signal);
  const pageShare = fetchDetails ? 50 : 100;

  const first = await call(() => fetchPage(1));
  const totalPages = Math.max(1, first.totalPages || 1);
  let loadedPages = 1;
  let loadedRows = first.rows.length;
  const reportPages = () =>
    onProgress?.({
      label: `Loading ${recordLabel}... ${loadedRows} loaded (page ${loadedPages} of ${totalPages})`,
      percent: Math.round((loadedPages / totalPages) * pageShare),
    });
  reportPages();

  const remainingPages = Array.from(
    { length: totalPages - 1 },
    (_, i) => i + 2,
  );
  const otherPages = await runInPool(remainingPages, async (page) => {
    const res = await call(() => fetchPage(page));
    loadedPages += 1;
    loadedRows += res.rows.length;
    reportPages();
    return res.rows;
  });

  const rows = [first.rows, ...otherPages].flat();
  if (!fetchDetails) return { rows, details: {} };

  const details = {};
  let loadedDetails = 0;
  const reportDetails = () =>
    onProgress?.({
      label: `Loading item lists... ${loadedDetails} / ${rows.length}`,
      percent: 50 + Math.round((rows.length ? loadedDetails / rows.length : 1) * 50),
    });
  reportDetails();

  await runInPool(chunk(rows, detailChunkSize), async (batch) => {
    Object.assign(details, await call(() => fetchDetails(batch)));
    loadedDetails += batch.length;
    reportDetails();
  });

  return { rows, details };
}
